import {
  AlignmentType,
  BorderStyle,
  Document,
  HeadingLevel,
  ImageRun,
  Packer,
  PageBreak,
  Paragraph,
  SectionType,
  Table,
  TableBorders,
  TableCell,
  TableOfContents,
  TableRow,
  TextRun,
  WidthType,
  convertInchesToTwip,
} from "docx";
import {
  buildCoverPageDocument,
  buildConfidentialityNoticeDocument,
  buildFindingDocument,
  buildProjectDetailsDocument,
  buildReportOutline,
  getSeverityPresentation,
  getRichTextBlocks,
} from "./formatFindingHtml.js";
import { stealthnetLogoDataUrl } from "../assets/stealthnetLogoData.js";

const PAGE_MARGIN_INCHES = 1;

const FONT_FAMILY = "Poppins";
const COLORS = {
  text: "202124",
  muted: "5F6368",
  border: "D7DDE5",
  tableFill: "F6F8FB",
};

const SEVERITY_TINTS = {
  "#6f42c1": "EEE7F8",
  "#c62828": "F9E5E5",
  "#ef6c00": "FDEEDF",
  "#1565c0": "E4EEF9",
  "#2e7d32": "E7F3E8",
};

const FONT_SIZES = {
  body: 24,
  title: 44,
  subtitle: 22,
  sectionHeading: 32,
  findingTitle: 36,
  subsectionHeading: 28,
  severity: 32,
};

const LINE_SPACING_TWIPS = 360;
const COVER_PAGE_WIDTH = 816;
const COVER_PAGE_HEIGHT = 1056;
const COVER_RENDER_SCALE = 2;
const COVER_EXPORT_MARGIN_INCHES = 0.38;
const COVER_EXPORT_MARGIN_PX = Math.round(COVER_EXPORT_MARGIN_INCHES * 96);
const COVER_EXPORT_WIDTH = COVER_PAGE_WIDTH - COVER_EXPORT_MARGIN_PX * 2;
const COVER_EXPORT_HEIGHT = COVER_PAGE_HEIGHT - COVER_EXPORT_MARGIN_PX * 2;

const FINDING_SECTION_METADATA = [
  { key: "definition", title: "Definition" },
  { key: "details", title: "Details" },
  { key: "location", title: "Location" },
  { key: "remediation", title: "Remediation" },
];

function createRun(text, options = {}) {
  return new TextRun({
    text,
    font: FONT_FAMILY,
    color: COLORS.text,
    size: FONT_SIZES.body,
    ...options,
  });
}

function createInlineRuns(line, baseOptions = {}) {
  const runs = [];
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let lastIndex = 0;
  let shouldApplyBreak = baseOptions.break;

  const consumeOptions = () => {
    if (!shouldApplyBreak) {
      return baseOptions;
    }

    const nextOptions = { ...baseOptions, break: shouldApplyBreak };
    shouldApplyBreak = undefined;
    return nextOptions;
  };

  for (const match of line.matchAll(pattern)) {
    const matchIndex = match.index ?? 0;
    if (matchIndex > lastIndex) {
      runs.push(createRun(line.slice(lastIndex, matchIndex), consumeOptions()));
    }

    const token = match[0];
    if (token.startsWith("**") && token.endsWith("**")) {
      runs.push(
        createRun(token.slice(2, -2), {
          ...consumeOptions(),
          bold: true,
        }),
      );
    } else if (token.startsWith("*") && token.endsWith("*")) {
      runs.push(
        createRun(token.slice(1, -1), {
          ...consumeOptions(),
          italics: true,
        }),
      );
    } else {
      runs.push(createRun(token, consumeOptions()));
    }

    lastIndex = matchIndex + token.length;
  }

  if (lastIndex < line.length) {
    runs.push(createRun(line.slice(lastIndex), consumeOptions()));
  }

  if (!runs.length) {
    runs.push(createRun("", consumeOptions()));
  }

  return runs;
}

function createParagraphsFromRichText(value, options = {}) {
  const blocks = getRichTextBlocks(value);
  if (!blocks.length) {
    return [createBodyParagraph("[Not generated yet]")];
  }

  return blocks.flatMap((block) => {
    if (block.type === "ul") {
      return block.items.map((item) =>
        new Paragraph({
          bullet: { level: 0 },
          spacing: { after: 120, line: LINE_SPACING_TWIPS },
          children: createInlineRuns(item),
        }),
      );
    }

    if (block.type === "ol") {
      return block.items.map((item, index) =>
        new Paragraph({
          spacing: { after: 120, line: LINE_SPACING_TWIPS },
          children: [createRun(`${index + 1}. `), ...createInlineRuns(item)],
        }),
      );
    }

    return [
      new Paragraph({
        spacing: { after: options.tight ? 80 : 140, line: LINE_SPACING_TWIPS },
        children: block.lines.flatMap((line, lineIndex) =>
          createInlineRuns(line, lineIndex > 0 ? { break: 1 } : {}),
        ),
      }),
    ];
  });
}

function createBodyParagraph(text, options = {}) {
  return new Paragraph({
    spacing: { after: options.after ?? 140, line: LINE_SPACING_TWIPS },
    alignment: options.alignment,
    children: createInlineRuns(text, options.runOptions),
  });
}

function createHeadingParagraph(text, heading, options = {}) {
  return new Paragraph({
    heading,
    pageBreakBefore: options.pageBreakBefore,
    spacing: {
      before: options.before ?? 0,
      after: options.after ?? 160,
      line: LINE_SPACING_TWIPS,
    },
    children: [
      createRun(text, {
        size: options.size,
        bold: true,
        color: options.color || COLORS.text,
      }),
    ],
  });
}

function createPageBreakParagraph() {
  return new Paragraph({
    spacing: { after: 0, line: LINE_SPACING_TWIPS },
    children: [new PageBreak()],
  });
}

function wrapCanvasText(context, text, maxWidth) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  if (!words.length) {
    return [];
  }

  const lines = [];
  let currentLine = words[0];

  for (let index = 1; index < words.length; index += 1) {
    const nextCandidate = `${currentLine} ${words[index]}`;
    if (context.measureText(nextCandidate).width <= maxWidth) {
      currentLine = nextCandidate;
      continue;
    }
    lines.push(currentLine);
    currentLine = words[index];
  }

  lines.push(currentLine);
  return lines;
}

function drawCoverLogo(context, x, y, scale, color) {
  context.save();
  context.translate(x, y);
  context.strokeStyle = color;
  context.fillStyle = color;
  context.lineWidth = 2.2 * scale;
  context.lineCap = "round";

  const circles = [
    { x: 12, y: 14, r: 5.5, alpha: 0.22 },
    { x: 28, y: 6, r: 4.5, alpha: 0.14 },
    { x: 30, y: 23, r: 4.5, alpha: 0.14 },
  ];

  circles.forEach((circle) => {
    context.beginPath();
    context.globalAlpha = circle.alpha;
    context.arc(circle.x * scale, circle.y * scale, circle.r * scale, 0, Math.PI * 2);
    context.fill();
  });

  context.globalAlpha = 1;
  context.beginPath();
  context.moveTo(16.8 * scale, 11.9 * scale);
  context.lineTo(23.4 * scale, 8.5 * scale);
  context.moveTo(16.8 * scale, 16.1 * scale);
  context.lineTo(24.2 * scale, 20.8 * scale);
  context.stroke();
  context.restore();
}

function loadBrowserImage(source) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = source;
  });
}

async function createCoverImageData(report) {
  if (typeof document === "undefined") {
    return null;
  }

  const cover = buildCoverPageDocument(report);
  const canvas = document.createElement("canvas");
  canvas.width = COVER_PAGE_WIDTH * COVER_RENDER_SCALE;
  canvas.height = COVER_PAGE_HEIGHT * COVER_RENDER_SCALE;
  const context = canvas.getContext("2d");

  if (!context) {
    return null;
  }

  context.scale(COVER_RENDER_SCALE, COVER_RENDER_SCALE);

  const gradient = context.createLinearGradient(0, 0, COVER_PAGE_WIDTH, COVER_PAGE_HEIGHT);
  gradient.addColorStop(0, "#081a29");
  gradient.addColorStop(0.4, "#0f3655");
  gradient.addColorStop(1, "#144f82");
  context.fillStyle = gradient;
  context.fillRect(0, 0, COVER_PAGE_WIDTH, COVER_PAGE_HEIGHT);

  const glowOne = context.createRadialGradient(690, 860, 40, 690, 860, 280);
  glowOne.addColorStop(0, "rgba(116, 194, 255, 0.34)");
  glowOne.addColorStop(1, "rgba(116, 194, 255, 0)");
  context.fillStyle = glowOne;
  context.fillRect(370, 530, 520, 520);

  const glowTwo = context.createRadialGradient(110, 180, 30, 110, 180, 250);
  glowTwo.addColorStop(0, "rgba(255, 255, 255, 0.16)");
  glowTwo.addColorStop(1, "rgba(255, 255, 255, 0)");
  context.fillStyle = glowTwo;
  context.fillRect(-120, -20, 420, 420);

  context.strokeStyle = "rgba(255, 255, 255, 0.08)";
  context.lineWidth = 1;
  for (let x = -80; x <= COVER_PAGE_WIDTH + 100; x += 54) {
    context.beginPath();
    context.moveTo(x, 0);
    context.lineTo(x + 120, COVER_PAGE_HEIGHT);
    context.stroke();
  }

  context.fillStyle = "rgba(255, 255, 255, 0.06)";
  context.beginPath();
  context.moveTo(COVER_PAGE_WIDTH - 170, 0);
  context.lineTo(COVER_PAGE_WIDTH, 0);
  context.lineTo(COVER_PAGE_WIDTH, 280);
  context.closePath();
  context.fill();

  context.fillStyle = "rgba(255, 255, 255, 0.82)";
  context.textAlign = "center";
  context.font = "600 16px Poppins, Arial, sans-serif";
  context.fillText(String(cover.dateDisplay).toUpperCase(), COVER_PAGE_WIDTH / 2, 104);

  context.fillStyle = "#ffffff";
  context.font = "700 54px Poppins, Arial, sans-serif";
  context.textAlign = "center";
  context.fillText(cover.title, COVER_PAGE_WIDTH / 2, 472);

  context.font = "500 24px Poppins, Arial, sans-serif";
  context.fillStyle = "rgba(255, 255, 255, 0.88)";
  wrapCanvasText(context, cover.subtitle, 520).forEach((line, index) => {
    context.fillText(line, COVER_PAGE_WIDTH / 2, 526 + index * 34);
  });

  context.textAlign = "center";
  context.font = "600 13px Poppins, Arial, sans-serif";
  context.fillStyle = "rgba(255, 255, 255, 0.7)";
  context.fillText(report.report_title || "StealthNet AI Security Report", COVER_PAGE_WIDTH / 2, 412);

  context.strokeStyle = "rgba(255, 255, 255, 0.18)";
  context.lineWidth = 1;
  context.beginPath();
  context.moveTo(72, COVER_PAGE_HEIGHT - 128);
  context.lineTo(COVER_PAGE_WIDTH - 72, COVER_PAGE_HEIGHT - 128);
  context.stroke();

  const logoWrapX = COVER_PAGE_WIDTH - 160;
  const logoWrapY = 54;
  const logoWrapSize = 92;
  context.fillStyle = "rgba(255, 255, 255, 0.08)";
  context.strokeStyle = "rgba(255, 255, 255, 0.14)";
  context.lineWidth = 1;
  context.beginPath();
  context.roundRect(logoWrapX, logoWrapY, logoWrapSize, logoWrapSize, 24);
  context.fill();
  context.stroke();

  try {
    const logoImage = await loadBrowserImage(stealthnetLogoDataUrl);
    const logoSize = 58;
    const logoX = logoWrapX + (logoWrapSize - logoSize) / 2;
    const logoY = logoWrapY + (logoWrapSize - logoSize) / 2;
    context.drawImage(logoImage, logoX, logoY, logoSize, logoSize);
  } catch {
    drawCoverLogo(context, logoWrapX + 18, logoWrapY + 20, 1.1, "#ffffff");
  }

  context.font = "600 11px Poppins, Arial, sans-serif";
  context.fillStyle = "rgba(255, 255, 255, 0.66)";
  context.textAlign = "left";
  context.fillText("PREPARED BY", 72, COVER_PAGE_HEIGHT - 44);
  context.font = "600 16px Poppins, Arial, sans-serif";
  context.fillStyle = "#ffffff";
  context.fillText(cover.brandName, 72, COVER_PAGE_HEIGHT - 22);

  context.textAlign = "right";
  context.font = "600 11px Poppins, Arial, sans-serif";
  context.fillStyle = "rgba(255, 255, 255, 0.66)";
  context.fillText("PREPARED FOR", COVER_PAGE_WIDTH - 72, COVER_PAGE_HEIGHT - 44);
  context.font = "600 16px Poppins, Arial, sans-serif";
  context.fillStyle = "#ffffff";
  context.fillText(cover.clientName, COVER_PAGE_WIDTH - 72, COVER_PAGE_HEIGHT - 22);

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!blob) {
    return null;
  }

  return blob.arrayBuffer();
}

async function createCoverSectionChildren(report) {
  const cover = buildCoverPageDocument(report);
  const imageData = await createCoverImageData(report);

  if (imageData) {
    return [
      new Paragraph({
        spacing: { before: 0, after: 0 },
        children: [
          new ImageRun({
            data: imageData,
            type: "png",
            transformation: {
              width: COVER_EXPORT_WIDTH,
              height: COVER_EXPORT_HEIGHT,
            },
          }),
        ],
      }),
    ];
  }

  return [
    createBodyParagraph(cover.dateDisplay, { alignment: AlignmentType.CENTER, after: 560 }),
    createHeadingParagraph(cover.title, HeadingLevel.TITLE, {
      size: 56,
      after: 180,
    }),
    createBodyParagraph(cover.subtitle, { alignment: AlignmentType.CENTER, after: 500 }),
    createBodyParagraph(`${cover.brandName} | ${cover.clientName}`, {
      alignment: AlignmentType.CENTER,
      after: 0,
    }),
  ];
}

function createBorderlessCell(children, widthPercent) {
  return new TableCell({
    width: {
      size: `${widthPercent}%`,
      type: WidthType.PERCENTAGE,
    },
    borders: {
      top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    },
    margins: {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    },
    children,
  });
}

function createFindingHeaderTable(finding) {
  const document = buildFindingDocument(finding);

  return new Table({
    width: {
      size: "100%",
      type: WidthType.PERCENTAGE,
    },
    borders: TableBorders.NONE,
    columnWidths: [74, 26].map((value) => `${value}%`),
    rows: [
      new TableRow({
        children: [
          createBorderlessCell(
            [
              new Paragraph({
                heading: HeadingLevel.HEADING_2,
                spacing: { after: 40, line: LINE_SPACING_TWIPS },
                children: [
                  createRun(document.title, {
                    size: FONT_SIZES.findingTitle,
                    bold: true,
                  }),
                ],
              }),
            ],
            74,
          ),
          createBorderlessCell(
            [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                spacing: { after: 40, line: LINE_SPACING_TWIPS },
                children: [
                  createRun(document.severity.label, {
                    size: FONT_SIZES.severity,
                    bold: true,
                    color: document.severity.color.replace("#", ""),
                  }),
                ],
              }),
            ],
            26,
          ),
        ],
      }),
    ],
  });
}

function createProjectTable(headers, values, { colorized = false } = {}) {
  return new Table({
    width: {
      size: "100%",
      type: WidthType.PERCENTAGE,
    },
    columnWidths: headers.map(() => `${100 / headers.length}%`),
    rows: [
      new TableRow({
        tableHeader: true,
        children: headers.map((header) => {
          const color = colorized ? header.color.replace("#", "") : COLORS.text;
          const fill = colorized ? SEVERITY_TINTS[header.color] || COLORS.tableFill : COLORS.tableFill;
          return new TableCell({
            width: {
              size: `${100 / headers.length}%`,
              type: WidthType.PERCENTAGE,
            },
            borders: {
              top: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
              bottom: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
              left: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
              right: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
            },
            shading: { fill, color: "auto" },
            children: [
              new Paragraph({
                alignment: colorized ? AlignmentType.CENTER : AlignmentType.LEFT,
                spacing: { after: 0, line: LINE_SPACING_TWIPS },
                children: [
                  createRun(header.label, {
                    bold: true,
                    color,
                  }),
                ],
              }),
            ],
          });
        }),
      }),
      new TableRow({
        children: values.map((value, index) => {
          const color = colorized ? headers[index].color.replace("#", "") : COLORS.text;
          return new TableCell({
            width: {
              size: `${100 / values.length}%`,
              type: WidthType.PERCENTAGE,
            },
            borders: {
              top: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
              bottom: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
              left: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
              right: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
            },
            children: [
              new Paragraph({
                alignment: colorized ? AlignmentType.CENTER : AlignmentType.LEFT,
                spacing: { after: 0, line: LINE_SPACING_TWIPS },
                children: [
                  createRun(String(value), {
                    bold: colorized,
                    color,
                  }),
                ],
              }),
            ],
          });
        }),
      }),
    ],
  });
}

function createDocumentChildren(report) {
  const confidentialityNotice = buildConfidentialityNoticeDocument(report);
  const projectDetails = buildProjectDetailsDocument(report);
  const outline = buildReportOutline(report);
  const findings = report.findings || [];

  const children = [
    new TableOfContents("Table of Contents", {
      headingStyleRange: "1-3",
      pageNumbersEntryLevelsRange: "1-3",
      hyperlink: true,
      cachedEntries: outline.tocEntries.map((entry) => ({
        title: entry.label,
        level: entry.level,
        page: entry.pageNumber,
      })),
      beginDirty: true,
    }),
    createPageBreakParagraph(),
    createHeadingParagraph("Confidentiality Notice", HeadingLevel.HEADING_1, {
      size: FONT_SIZES.sectionHeading,
    }),
    ...createParagraphsFromRichText(confidentialityNotice.body),
    createPageBreakParagraph(),
    createHeadingParagraph("Project Details", HeadingLevel.HEADING_1, {
      size: FONT_SIZES.sectionHeading,
    }),
    createHeadingParagraph("Overview", HeadingLevel.HEADING_2, {
      size: FONT_SIZES.subsectionHeading,
      after: 120,
    }),
    ...createParagraphsFromRichText(projectDetails.overview),
    createHeadingParagraph("Dates", HeadingLevel.HEADING_2, {
      size: FONT_SIZES.subsectionHeading,
      before: 80,
      after: 100,
    }),
    createProjectTable(
      projectDetails.dates.map((item) => ({ label: item.label })),
      projectDetails.dates.map((item) => item.value),
    ),
    createHeadingParagraph("Findings", HeadingLevel.HEADING_2, {
      size: FONT_SIZES.subsectionHeading,
      before: 160,
      after: 100,
    }),
    createProjectTable(
      projectDetails.findings.map((item) => ({
        label: item.label,
        color: getSeverityPresentation(item.label).color,
      })),
      projectDetails.findings.map((item) => item.value),
      { colorized: true },
    ),
    createHeadingParagraph("Scope", HeadingLevel.HEADING_2, {
      size: FONT_SIZES.subsectionHeading,
      before: 160,
      after: 120,
    }),
  ];

  if (projectDetails.scope) {
    children.push(...createParagraphsFromRichText(projectDetails.scope));
  } else {
    children.push(createBodyParagraph("[Add scope here]"));
  }

  children.push(
    createPageBreakParagraph(),
    createHeadingParagraph("Executive Summary", HeadingLevel.HEADING_1, {
      size: FONT_SIZES.sectionHeading,
    }),
    ...createParagraphsFromRichText(report.executive_summary || "[Not generated yet]"),
    createPageBreakParagraph(),
    createHeadingParagraph("Methodology", HeadingLevel.HEADING_1, {
      size: FONT_SIZES.sectionHeading,
    }),
    ...createParagraphsFromRichText(report.methodology_text || "[Not generated yet]"),
    createPageBreakParagraph(),
    createHeadingParagraph("Findings", HeadingLevel.HEADING_1, {
      size: FONT_SIZES.sectionHeading,
    }),
  );

  if (!findings.length) {
    children.push(createBodyParagraph("No findings are in this report yet."));
    return children;
  }

  findings.forEach((finding, index) => {
    if (index > 0) {
      children.push(createPageBreakParagraph());
    }

    const findingDocument = buildFindingDocument(finding);

    children.push(createFindingHeaderTable(finding));

    FINDING_SECTION_METADATA.forEach((section) => {
      const matchingSection = findingDocument.sections.find((item) => item.key === section.key);
      children.push(
        createHeadingParagraph(section.title, HeadingLevel.HEADING_3, {
          size: FONT_SIZES.subsectionHeading,
          before: 140,
          after: 80,
        }),
        ...createParagraphsFromRichText(matchingSection?.value || "[Not generated yet]"),
      );
    });
  });

  return children;
}

export async function buildReportDocxBlob(report) {
  const coverChildren = await createCoverSectionChildren(report);
  const document = new Document({
    features: {
      updateFields: true,
    },
    styles: {
      default: {
        document: {
          paragraph: {
            run: {
              font: FONT_FAMILY,
              size: FONT_SIZES.body,
              color: COLORS.text,
            },
            spacing: {
              line: LINE_SPACING_TWIPS,
            },
          },
        },
        title: {
          run: {
            font: FONT_FAMILY,
            size: FONT_SIZES.title,
            color: COLORS.text,
            bold: true,
          },
          paragraph: {
            spacing: {
              line: LINE_SPACING_TWIPS,
            },
          },
        },
        heading1: {
          run: {
            font: FONT_FAMILY,
            size: FONT_SIZES.sectionHeading,
            color: COLORS.text,
            bold: true,
          },
          paragraph: {
            spacing: {
              line: LINE_SPACING_TWIPS,
            },
          },
        },
        heading2: {
          run: {
            font: FONT_FAMILY,
            size: FONT_SIZES.findingTitle,
            color: COLORS.text,
            bold: true,
          },
          paragraph: {
            spacing: {
              line: LINE_SPACING_TWIPS,
            },
          },
        },
        heading3: {
          run: {
            font: FONT_FAMILY,
            size: FONT_SIZES.subsectionHeading,
            color: COLORS.text,
            bold: true,
          },
          paragraph: {
            spacing: {
              line: LINE_SPACING_TWIPS,
            },
          },
        },
      },
      paragraphStyles: [
        {
          id: "TOC1",
          name: "TOC 1",
          basedOn: "Normal",
          run: {
            font: FONT_FAMILY,
            size: FONT_SIZES.body,
            color: COLORS.text,
            bold: true,
          },
          paragraph: {
            spacing: {
              line: LINE_SPACING_TWIPS,
            },
          },
        },
        {
          id: "TOC2",
          name: "TOC 2",
          basedOn: "Normal",
          run: {
            font: FONT_FAMILY,
            size: FONT_SIZES.body,
            color: COLORS.text,
          },
          paragraph: {
            indent: {
              left: 360,
            },
            spacing: {
              line: LINE_SPACING_TWIPS,
            },
          },
        },
        {
          id: "TOC3",
          name: "TOC 3",
          basedOn: "Normal",
          run: {
            font: FONT_FAMILY,
            size: FONT_SIZES.body,
            color: COLORS.text,
          },
          paragraph: {
            indent: {
              left: 720,
            },
            spacing: {
              line: LINE_SPACING_TWIPS,
            },
          },
        },
      ],
    },
    sections: [
      {
        properties: {
          type: SectionType.NEXT_PAGE,
          page: {
            margin: {
              top: convertInchesToTwip(COVER_EXPORT_MARGIN_INCHES),
              right: convertInchesToTwip(COVER_EXPORT_MARGIN_INCHES),
              bottom: convertInchesToTwip(COVER_EXPORT_MARGIN_INCHES),
              left: convertInchesToTwip(COVER_EXPORT_MARGIN_INCHES),
            },
          },
        },
        children: coverChildren,
      },
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(PAGE_MARGIN_INCHES),
              right: convertInchesToTwip(PAGE_MARGIN_INCHES),
              bottom: convertInchesToTwip(PAGE_MARGIN_INCHES),
              left: convertInchesToTwip(PAGE_MARGIN_INCHES),
            },
          },
        },
        children: createDocumentChildren(report),
      },
    ],
  });

  return Packer.toBlob(document);
}
